import React from 'react';

interface Props {
  /**
   * Interrupt the screen reader rather than waiting for a pause. Reserve it for states the user
   * has to act on — a failure, a lost connection — since an assertive region talks over them.
   */
  assertive: boolean;
  /** `span` by default; `div` for a region that wraps block content. */
  as?: 'span' | 'div';
  className?: string;
  children: React.ReactNode;
}

/**
 * A live region whose urgency can change over its lifetime.
 *
 * The `key` is the load-bearing part: `role='alert'` announces on mount, and changing `aria-live`
 * on an element already in the tree is unreliable across screen readers. Keying on urgency
 * remounts the node when it flips, so the assertive state actually interrupts.
 *
 * Scope the region to the text worth announcing. Anything that rerenders on a timer — a relative
 * timestamp, a countdown — belongs outside it, or the region repeats itself on every tick.
 */
export default function LiveRegion({assertive, as: Element = 'span', className, children}: Props) {
  return (
    <Element
      key={assertive ? 'assertive' : 'polite'}
      role={assertive ? 'alert' : 'status'}
      className={className}
    >
      {children}
    </Element>
  );
}
